import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { IAppState } from '@myStore/app.state';
import {
  AddHolidaysToPeriodFailure,
  AddHolidaysToPeriodRequest,
  AddHolidaysToPeriodSuccess,
  AddHolidaysToSeveralGroupsFailure,
  AddHolidaysToSeveralGroupsRequest,
  AddHolidaysToSeveralGroupsSuccess,
  CreatePeriodFailure,
  CreatePeriodRequest,
  CreatePeriodSuccess,
  DeletePeriodFailure,
  DeletePeriodHolidaysFailure,
  DeletePeriodHolidaysRequest,
  DeletePeriodHolidaysSuccess,
  DeletePeriodRequest,
  DeletePeriodSuccess,
  EPeriodsChangesActions,
  SetPeriodThemeFailure,
  SetPeriodThemeRequest,
  SetPeriodThemeSuccess,
  UpdatePeriodFailure,
  UpdatePeriodRequest,
  UpdatePeriodSuccess,
} from '@myStore/periods-changes/periods-changes.actions';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { PeriodsChangesService } from '@services/periods-changes/periods-changes.service';
import { ToastrService } from 'ngx-toastr';
import { catchError, map, switchMap } from 'rxjs/operators';

@Injectable()
export class PeriodsChangesEffects {
  constructor(
    private _actions$: Actions,
    private _store: Store<IAppState>,
    private _periodsChangesService: PeriodsChangesService,
    private _router: Router,
    private _toastr: ToastrService
  ) {}

  $create = createEffect(
    () => {
      return this._actions$.pipe(
        ofType<CreatePeriodRequest>(EPeriodsChangesActions.CreatePeriodRequest),
        switchMap(action => {
          return this._periodsChangesService.create(action.data).pipe(
            map(res => {
              this._toastr.success(
                `Период #${res.number} успешно создан!`,
                '',
                {
                  timeOut: 3500,
                }
              );
              this._store.dispatch(new CreatePeriodSuccess());
            }),
            catchError(err => {
              this._toastr.error(
                `${
                  err?.error?.detail
                    ? err?.error?.detail
                    : 'При создании периода возникла ошибка'
                }`,
                'Что-то пошло не так',
                { timeOut: 3500 }
              );
              this._store.dispatch(new CreatePeriodFailure(err.error));
              return err.error;
            })
          );
        })
      );
    },
    { dispatch: false }
  );

  $update = createEffect(
    () => {
      return this._actions$.pipe(
        ofType<UpdatePeriodRequest>(EPeriodsChangesActions.UpdatePeriodRequest),
        switchMap(action => {
          return this._periodsChangesService
            .update(action.periodId, action.data)
            .pipe(
              map(res => {
                this._toastr.success(
                  `Период #${res.number} успешно отредактирован!`,
                  '',
                  {
                    timeOut: 3500,
                  }
                );
                this._store.dispatch(new UpdatePeriodSuccess());
              }),
              catchError(err => {
                this._toastr.error(
                  `${
                    err?.error?.detail
                      ? err?.error?.detail
                      : 'При редактировании периода возникла ошибка'
                  }`,
                  'Что-то пошло не так',
                  { timeOut: 3500 }
                );
                this._store.dispatch(new UpdatePeriodFailure(err.error));
                return err.error;
              })
            );
        })
      );
    },
    { dispatch: false }
  );

  $delete = createEffect(
    () => {
      return this._actions$.pipe(
        ofType<DeletePeriodRequest>(EPeriodsChangesActions.DeletePeriodRequest),
        switchMap(action => {
          return this._periodsChangesService.delete(action.periodId).pipe(
            map(() => {
              this._toastr.success(`Период успешно удален!`, '', {
                timeOut: 3500,
              });
              this._store.dispatch(new DeletePeriodSuccess());
            }),
            catchError(err => {
              this._toastr.error(
                `${
                  err?.error?.detail
                    ? err?.error?.detail
                    : 'При удалении периода возникла ошибка'
                }`,
                'Что-то пошло не так',
                { timeOut: 3500 }
              );
              this._store.dispatch(new DeletePeriodFailure(err.error));
              return err.error;
            })
          );
        })
      );
    },
    { dispatch: false }
  );

  $addHolidays = createEffect(
    () => {
      return this._actions$.pipe(
        ofType<AddHolidaysToPeriodRequest>(
          EPeriodsChangesActions.AddHolidaysToPeriodRequest
        ),
        switchMap(action => {
          return this._periodsChangesService
            .putUpdate(action.periodId, action.data)
            .pipe(
              map(res => {
                this._toastr.success(
                  `Каникулы в период #${res.number} успешно добавлены!`,
                  '',
                  {
                    timeOut: 3500,
                  }
                );
                this._store.dispatch(new AddHolidaysToPeriodSuccess());
              }),
              catchError(err => {
                this._toastr.error(
                  `${
                    err?.error?.detail
                      ? err?.error?.detail
                      : 'При добавлении каникул в период возникла ошибка'
                  }`,
                  'Что-то пошло не так',
                  { timeOut: 3500 }
                );
                this._store.dispatch(new AddHolidaysToPeriodFailure(err.error));
                return err.error;
              })
            );
        })
      );
    },
    { dispatch: false }
  );

  $addMassHolidaysToGroups = createEffect(
    () => {
      return this._actions$.pipe(
        ofType<AddHolidaysToSeveralGroupsRequest>(
          EPeriodsChangesActions.AddHolidaysToSeveralGroupsRequest
        ),
        switchMap(action => {
          return this._periodsChangesService.addMassHolidays(action.data).pipe(
            map(() => {
              this._toastr.success(`Каникулы успешно добавлены!`, '', {
                timeOut: 3500,
              });
              this._store.dispatch(new AddHolidaysToSeveralGroupsSuccess());
            }),
            catchError(err => {
              this._store.dispatch(
                new AddHolidaysToSeveralGroupsFailure(err.error)
              );

              if (err.error.weeks_offset) {
                this._toastr.error(`${err.error.weeks_offset}!`, '', {
                  timeOut: 4000,
                });
              }

              if (err.error.offset_date) {
                this._toastr.error(`${err.error.offset_date}!`, '', {
                  timeOut: 6000,
                });
              }

              return err.error;
            })
          );
        })
      );
    },
    { dispatch: false }
  );

  $deleteHolidays = createEffect(
    () => {
      return this._actions$.pipe(
        ofType<DeletePeriodHolidaysRequest>(
          EPeriodsChangesActions.DeletePeriodHolidaysRequest
        ),
        switchMap(action => {
          return this._periodsChangesService
            .deleteHolidays(action.periodId)
            .pipe(
              map(() => {
                this._toastr.success(
                  `Каникулы в период #${action.number} успешно удалены!`,
                  '',
                  {
                    timeOut: 3500,
                  }
                );
                this._store.dispatch(new DeletePeriodHolidaysSuccess());
              }),
              catchError(err => {
                this._toastr.error(
                  `${
                    err?.error?.detail
                      ? err?.error?.detail
                      : 'При удалении каникул возникла ошибка'
                  }`,
                  'Что-то пошло не так',
                  { timeOut: 3500 }
                );
                this._store.dispatch(
                  new DeletePeriodHolidaysFailure(err.error)
                );
                return err.error;
              })
            );
        })
      );
    },
    { dispatch: false }
  );

  $setTheme = createEffect(
    () => {
      return this._actions$.pipe(
        ofType<SetPeriodThemeRequest>(
          EPeriodsChangesActions.SetPeriodThemeRequest
        ),
        switchMap(action => {
          return this._periodsChangesService
            .setTheme(action.id, action.data)
            .pipe(
              map(res => {
                this._toastr.success('Тема успешно изменена!');
                this._store.dispatch(new SetPeriodThemeSuccess(res));
              }),
              catchError(err => {
                this._toastr.error(
                  `${
                    err?.error?.detail
                      ? err?.error?.detail
                      : 'При изменении темы возникла ошибка'
                  }`,
                  'Что-то пошло не так',
                  { timeOut: 3500 }
                );
                this._store.dispatch(new SetPeriodThemeFailure(err.error));
                return err.error;
              })
            );
        })
      );
    },
    { dispatch: false }
  );
}
